import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { MAX_RETRY_COUNT, RETRY_INTERVAL, UserCapabilities, UserProfile, UserRole } from '@qvs/core';
import { select, Store } from '@ngrx/store';
import { catchError, distinctUntilChanged, filter, map, switchMap, take } from 'rxjs/operators';
import { selectIsAuthenticated } from '@qvs/core/auth';
import { selectUserCapabilities, selectUserRole } from '../store';
import * as wizeMobileApi from '@wize-mobile-api';
import { UserMobile } from '@wize-mobile-api';
import { UserMapper } from '../mapper';
import { HttpContext, HttpErrorResponse, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { DebugService } from '~/shared/services/debug.service';
import { environment } from '#environment';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  retryInterval = 500;
  isSessionActiveRetryCount = 0;
  private userCapabilities: UserCapabilities = {};
  private role: UserRole;

  constructor(
    private readonly store: Store,
    private readonly wizeApi: wizeMobileApi.ThirdpartyMobileService,
    private readonly basicDataApi: wizeMobileApi.BasicDataMobileService,
    private readonly debugService: DebugService
  ) {
    this.store.pipe(select(selectUserRole)).subscribe((val) => (this.role = val));
    this.store.select(selectUserCapabilities).subscribe((userCapabilities) => (this.userCapabilities = userCapabilities));
  }

  public get userRole(): UserRole {
    return this.role;
  }

  public get capabilities(): UserCapabilities {
    return this.userCapabilities;
  }

  public loadProfile(): Observable<UserProfile | undefined> {
    return this.store
      .select(selectIsAuthenticated)
      .pipe(
        filter((isAuthenticated) => !!isAuthenticated),
        take(1)
      )
      .pipe(
        switchMap(() =>
          this.wizeApi
            .getCurrentUserProfile('response', false, { context: new HttpContext().set(RETRY_INTERVAL, this.retryInterval) })
            .pipe(
              switchMap((response: HttpResponse<UserMobile>) => {
                if (response.status === HttpStatusCode.Ok) {
                  return of({
                    role: UserMapper.fromWizeUserProfileRole(response.body),
                    person: UserMapper.fromWizeUser(response.body),
                    capabilities: UserMapper.fromUserProfileCapabilities(response.body),
                    contacts: UserMapper.fromWizeUserProfileContacts(response.body)
                  });
                }
                return throwError(() => new Error('PROFILE_LOADING_ERROR'));
              }),
              catchError((e) => throwError(() => e))
            )
        )
      );
  }

  public checkUserSessionIsActive(): Observable<boolean> {
    return this.basicDataApi
      .listCurrencyTypes('response', false, { context: new HttpContext().set(MAX_RETRY_COUNT, this.isSessionActiveRetryCount) })
      .pipe(
        switchMap((response: HttpResponse<Array<string>>) => of(response.status === HttpStatusCode.Ok)),
        catchError((error) => {
          if (
            error instanceof HttpErrorResponse &&
            [HttpStatusCode.Forbidden, HttpStatusCode.Unauthorized].includes(error.status)
          ) {
            return of(false);
          }
          this.debugService.logError('check user session failed with error:', error);
          // if there is another error like no internet connection we assume that user session is still active and return true
          // TODO 31-May-2023/braune: we could define more strictly which errors are considered "acceptable"
          return of(true);
        })
      );
  }

  public getArePaymentsAuthorized(): Observable<boolean> {
    if (!environment.featureFlags.checkPaymentsAuthorized) {
      return of(true);
    }
    return this.store.select(selectUserCapabilities).pipe(
      map((capabilities) => capabilities?.canCreateNewPaymentOrders),
      distinctUntilChanged()
    );
  }

  loadUserProfileImage(contactId: string): Observable<{ staffId: string; image: string }> {
    return this.wizeApi.getUserProfileImage(contactId).pipe(
      switchMap((response) =>
        of({
          staffId: contactId,
          image: response?.icon
        })
      ),
      catchError((_e) =>
        of({
          staffId: contactId,
          image: null
        })
      )
    );
  }

  public init(): Promise<void> {
    return Promise.resolve();
  }
}
